import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen theme-bg">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-amber-700 hover:text-amber-900 hover:bg-amber-100 btn-glow rounded-lg"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <Card className="card-glow p-8 bg-white shadow-lg">
          <h1 className="text-4xl font-bold text-amber-900 mb-4">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-8">Last Updated: December 2025</p>

          <div className="prose prose-amber max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">1. Introduction</h2>
              <p className="text-gray-700">
                Lyceum ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-amber-800 mb-2 mt-4">Personal Information</h3>
              <p className="text-gray-700 mb-2">We collect information that you provide directly to us:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Name and email address</li>
                <li>Account credentials (password is encrypted)</li>
                <li>Profile picture (optional)</li>
                <li>Payment information (processed securely through Stripe)</li>
              </ul>

              <h3 className="text-xl font-semibold text-amber-800 mb-2 mt-4">Usage Information</h3>
              <p className="text-gray-700 mb-2">We automatically collect certain information about your device and usage:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Course progress and quiz scores</li>
                <li>Debate history and performance metrics</li>
                <li>Learning preferences and settings</li>
                <li>Device information and browser type</li>
                <li>IP address and location data</li>
              </ul>

              <h3 className="text-xl font-semibold text-amber-800 mb-2 mt-4">AI-Generated Content</h3>
              <p className="text-gray-700">
                Your interactions with AI features (course generation, debates, chat) are stored to provide 
                personalized learning experiences and improve our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-2">We use collected information for:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Providing, maintaining, and improving the Service</li>
                <li>Creating and managing your account</li>
                <li>Processing payments and preventing fraud</li>
                <li>Sending you updates, newsletters, and promotional materials (with your consent)</li>
                <li>Responding to your comments and questions</li>
                <li>Analyzing usage patterns to improve user experience</li>
                <li>Personalizing course recommendations and content</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-2">We may share your information in the following circumstances:</p>
              
              <h3 className="text-xl font-semibold text-amber-800 mb-2 mt-4">Service Providers</h3>
              <p className="text-gray-700">
                We work with third-party service providers who perform services on our behalf:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mt-2">
                <li>Stripe (payment processing)</li>
                <li>Google (OAuth authentication)</li>
                <li>Cloud hosting providers</li>
                <li>AI/ML service providers</li>
              </ul>

              <h3 className="text-xl font-semibold text-amber-800 mb-2 mt-4">Legal Requirements</h3>
              <p className="text-gray-700">
                We may disclose your information if required by law or in response to valid legal requests by 
                public authorities.
              </p>

              <h3 className="text-xl font-semibold text-amber-800 mb-2 mt-4">Business Transfers</h3>
              <p className="text-gray-700">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                to the acquiring entity.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">5. Data Security</h2>
              <p className="text-gray-700">
                We implement appropriate technical and organizational measures to protect your personal information, 
                including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mt-2">
                <li>Encrypted data transmission (HTTPS/TLS)</li>
                <li>Secure password hashing</li>
                <li>Regular security audits</li>
                <li>Access controls and authentication</li>
              </ul>
              <p className="text-gray-700 mt-2">
                However, no method of transmission over the Internet is 100% secure. While we strive to protect 
                your personal information, we cannot guarantee its absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">6. Data Retention</h2>
              <p className="text-gray-700">
                We retain your personal information for as long as necessary to provide the Service and fulfill 
                the purposes outlined in this Privacy Policy. When you delete your account, we will delete or 
                anonymize your personal information within 30 days, except where we are required to retain it 
                for legal compliance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">7. Your Privacy Rights</h2>
              <p className="text-gray-700 mb-2">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Receive your data in a portable format</li>
              </ul>
              <p className="text-gray-700 mt-2">
                To exercise these rights, contact us at help@mylyceum.org
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">8. Cookies and Tracking</h2>
              <p className="text-gray-700">
                We use cookies and similar tracking technologies to improve your experience, analyze usage, 
                and remember your preferences. You can control cookies through your browser settings, but 
                disabling them may affect functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">9. Children's Privacy</h2>
              <p className="text-gray-700">
                Our Service is not intended for children under 13 years of age. We do not knowingly collect 
                personal information from children under 13. If you are a parent or guardian and believe your 
                child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">10. International Data Transfers</h2>
              <p className="text-gray-700">
                Your information may be transferred to and processed in countries other than your country of 
                residence. These countries may have data protection laws different from your country. By using 
                the Service, you consent to such transfers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage 
                you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-amber-900 mb-3">12. Contact Us</h2>
              <p className="text-gray-700">
                If you have questions or concerns about this Privacy Policy, please contact us at:
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Email:</strong> help@mylyceum.org<br />
                <strong>Service:</strong> Lyceum
              </p>
            </section>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
